import React, { useState, useEffect, useContext } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { AiFillThunderbolt } from 'react-icons/ai';
import { PiPlugChargingFill, PiStopBold } from 'react-icons/pi';
import { VscDebugStart } from 'react-icons/vsc';
import { MdChevronLeft, MdChevronRight } from 'react-icons/md';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { Modal } from 'antd';
import styles from './HomeCharging.module.css';
import { BreathAPIs } from '../../../../APIs/BreathAPIs';
import { BookingsContext } from '../BookingContext';
import { useWebSocket } from '../../../../Utils/WebSocketContext';
import LoadingBooking from '../../../../BookingHistory/LoadingBooking';
import { faBolt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { HiRefresh } from "react-icons/hi";
import { Link } from 'react-router-dom';
import { IoInformationCircle } from "react-icons/io5";


const HomeCharging = ({ userID, subLocations, isAutoPayEnabledForUser }) => {
  const [homeChargers, setHomeChargers] = useState(null);
  const [activeSessions, setActiveSessions] = useState({});
  const [livePower, setLivePower] = useState({});
  const [liveEnergy, setLiveEnergy] = useState({});
  const [submitting, setSubmitting] = useState({});
  const [unlocking, setUnlocking] = useState({});
  const [unlockModalCharger, setUnlockModalCharger] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [recentSessionLoading, setRecentSessionLoading] = useState(false);
  const [lastSessionData, setLastSessionData] = useState({});
  const [period, setPeriod] = useState('week');
  const [periodOffset, setPeriodOffset] = useState(0);
  const [activeBookings, setActiveBookings] = useState([])
  const [showRecent, setShowRecent] = useState(false);
  const [bookingHistory, setBookingHistory] = useState([]);
  const [allBookingHistory, setAllBookingHistory] = useState([]);
  const [bookingHistoryMeta, setBookingHistoryMeta] = useState({ total: 0, page: 1, pageSize: 4 });
  const [bookingHistoryLoading, setBookingHistoryLoading] = useState(false);
  const [onGoingUserBookings, setOnGoingUserBookings] = useState([])
  const [refreshing, setRefreshing] = useState(false);

  const { refetchBookings } = useContext(BookingsContext);
  const webSocket = useWebSocket();

  const homeLocationId = subLocations?.[0]?._id;
  // console.log("active: ", subLocations)

  const fetchHomeChargers = async () => {
    setRefreshing(true);
    if (!homeLocationId) return;
    try {
      const res = await BreathAPIs.getAllChargersByLocation(homeLocationId);
      setHomeChargers(res.data.chargers || []);
    } catch {
      setHomeChargers([]);
    }finally{
      setRefreshing(false);
    }
  };

  const getPeriodRange = (p, offset) => {
    const now = new Date();
    if (p === 'week') {
      const start = new Date(now);
      start.setDate(now.getDate() - now.getDay() + offset * 7);
      start.setHours(0, 0, 0, 0);
      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      end.setHours(23, 59, 59, 999);
      return { start, end };
    }
    if (p === 'month') {
      const d = new Date(now.getFullYear(), now.getMonth() + offset, 1);
      return {
        start: new Date(d.getFullYear(), d.getMonth(), 1),
        end: new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999),
      };
    }
    const yr = now.getFullYear() + offset;
    return { start: new Date(yr, 0, 1), end: new Date(yr, 11, 31, 23, 59, 59, 999) };
  };

  const fetchHistory = async (chargers) =>{
    try{
      if ((!chargers || chargers.length === 0) && homeChargers == null) return;
    if(!chargers || chargers.length === 0){
      chargers = homeChargers  
    }
    const homeIds = new Set(chargers.map((c) => c._id));
    setHistoryLoading(true);
    const { start, end } = getPeriodRange(period, periodOffset);
      // const res = await BreathAPIs.getBookingHistory(1, 30);
      const res = await BreathAPIs.getLastSessionResponse([...homeIds], userID, new Date(start), new Date(end))
      const bookings = res.data.bookings || [];
      const filtered = bookings.filter((b) => {
        const cid = typeof b.charger === 'object' ? b.charger?._id : b.charger;
        return homeIds.has(cid);
      });
      setHistory(filtered);


      const energyMap = {};
      const completedByCharger = {};
      filtered.forEach((b) => {
        const cid = typeof b.charger === 'object' ? b.charger?._id : b.charger;
        if ( b.bookingStatus === 'Completed') {
          if (!completedByCharger[cid] || new Date(b.startTime) > new Date(completedByCharger[cid].startTime)) {
            completedByCharger[cid] = b;
          }
        }
      });
      await Promise.all(
        Object.entries(completedByCharger).map(async ([cid, booking]) => {
          try {
            const sr = await BreathAPIs.getBookingSession(booking._id);
            energyMap[cid] = sr.data[0]?.consumedEnergy ?? sr.data[0]?.energyDelivered ?? null;
          } catch {
            energyMap[cid] = null;
          }
        })
      );
    }catch(error){
      console.log("error in fetching history: ", error.message)
    }finally {
      setHistoryLoading(false);
    }
  }

  const fetchLastAndInprogress = async (chargers) => {
    if ((!chargers || chargers.length === 0) && homeChargers == null) return;
    if(!chargers || chargers.length === 0){
      chargers = homeChargers  
    }
    const homeIds = new Set(chargers.map((c) => c._id));
    setRecentSessionLoading(true);
    try {
      // const { start, end } = getPeriodRange(period, periodOffset);
      // // const res = await BreathAPIs.getBookingHistory(1, 30);
      // const res = await BreathAPIs.getLastSessionResponse([...homeIds], userID, new Date(start), new Date(end))
      // const bookings = res.data.bookings || [];
      // const filtered = bookings.filter((b) => {
      //   const cid = typeof b.charger === 'object' ? b.charger?._id : b.charger;
      //   return homeIds.has(cid);
      // });
      // setHistory(filtered);

      let ongoingBookings = await BreathAPIs.getUpcomingBookings()
      let ongoingBookingsData = ongoingBookings.data
      setOnGoingUserBookings(ongoingBookingsData)

      let lastSessionResponse = await BreathAPIs.getLastSessionResponse([...homeIds], userID)
      // console.log("last session response : ", lastSessionResponse.data.bookings)
      setLastSessionData(lastSessionResponse.data.bookings)

      const now = new Date();
      const newActive = {};
      const activeBookingsData = []
      ongoingBookingsData.forEach((b) => {
        const started = new Date(b.startTime) <= now;
        const notEnded = !b.endedAt && (!b.endTime || new Date(b.endTime) > now);
        if (started && notEnded) {
          const cid = typeof b.charger === 'object' ? b.charger?._id : b.charger;
          newActive[cid] = b._id;
          activeBookingsData.push(b)
        }
      });
      setActiveSessions((prev) => ({ ...prev, ...newActive }));
      setActiveBookings(activeBookingsData)

      // fetch energy for the most recent completed session per charger
      // const energyMap = {};
      // const completedByCharger = {};
      // filtered.forEach((b) => {
      //   const cid = typeof b.charger === 'object' ? b.charger?._id : b.charger;
      //   if (!newActive[cid] && b.bookingStatus === 'Completed') {
      //     if (!completedByCharger[cid] || new Date(b.startTime) > new Date(completedByCharger[cid].startTime)) {
      //       completedByCharger[cid] = b;
      //     }
      //   }
      // });
      // await Promise.all(
      //   Object.entries(completedByCharger).map(async ([cid, booking]) => {
      //     try {
      //       const sr = await BreathAPIs.getBookingSession(booking._id);
      //       energyMap[cid] = sr.data[0]?.consumedEnergy ?? sr.data[0]?.energyDelivered ?? null;
      //     } catch {
      //       energyMap[cid] = null;
      //     }
      //   })
      // );
      // setLastSessionEnergy(energyMap);
    } catch (error) {
      console.log("error in fetchLastAndInprogress: ", error.message)
      // silently fail
    } finally {
      setRecentSessionLoading(false);
    }
  };

    useEffect(()=>{
    // const { start, end } = getPeriodRange(period, periodOffset);
    fetchHistory()
    // fetchLastAndInprogress()

    // console.log("period start and end: ", start, end) 
  }, [period, periodOffset, homeChargers])

  useEffect(()=>{
  }, [liveEnergy, livePower])
  

  useEffect(() => {
    
    fetchHomeChargers();
  }, [homeLocationId]);

  useEffect(() => {
    if (homeChargers) fetchLastAndInprogress(homeChargers);
  }, [homeChargers]);

  // Fetch initial live energy when active sessions are (re)discovered
  useEffect(() => {
    Object.entries(activeSessions).forEach(async ([chargerId, bookingId]) => {
      if (!bookingId || bookingId === true) return;
      try {
        const res = await BreathAPIs.getEnergtReport(bookingId);
        const active = res.data?.activeSession;
        if (active) {
          setLivePower((p) => ({ ...p, [chargerId]: active.activeSessionPower }));
          setLiveEnergy((p) => ({ ...p, [chargerId]: active.activeSessionEnergy }));
        }
      } catch {}
    });
  }, [activeSessions]);

  useEffect(() => {
    if (!webSocket) return;
    const handler = (event) => {
      try {
        const msg = JSON.parse(event.data);
        if (msg.type === 'refetch_data') {
          setTimeout(() => {
            fetchHomeChargers();
            refetchBookings?.();
          }, 1000);
        } else if (msg.type === 'session_update_event_broadcast') {
          const { power, cumulativeEnergy, edrvSessionID } = msg.data || {};
          // console.log("event data: ", power, cumulativeEnergy, edrvSessionID)
          // console.log("active bookings; ", activeBookings)
          setActiveSessions((current) => {
            Object.keys(current).forEach((chargerId) => {
              let activeBookingsData = [...activeBookings]
              activeBookingsData.map((b) => {
                if(String(b.charger._id) == String(chargerId) && b.currentSessionEdrvID == edrvSessionID){
                  setLivePower((p) => ({ ...p, [chargerId]: power }));
                  setLiveEnergy((p) => ({ ...p, [chargerId]: cumulativeEnergy }));
                }
                
              })
              
            });
            return current;
          });
        }
        else if(msg.type === 'chargestation.connector_status.updated'){
          msg.data?.connectors?.forEach((conn) => {
            const targetId = conn.meta_data.breatheConnectorId;
            const newStatus = conn.status;
            let homeChargerData = [...homeChargers]
            homeChargerData.forEach((charger) =>{
              const foundConnector = charger.connector.find(
                (conn) => conn._id.toString() === targetId.toString()
              );
              if(foundConnector){
                foundConnector.status = newStatus
              }
            })
            setHomeChargers(homeChargerData)

          })
            // for (let i = 0; i < msg.data.connectors.length; i++) {
            //   let homeChargerData = [...homeChargers]

            // // if (breatheConnectorId === msg.data.connectors[i].meta_data.breatheConnectorId) {
            // //   let connectorStatus = msg.data.connectors[i].status
              
              
            // }
          }
        
        else if(msg.type === 'fetch_history'){
          fetchHistory(homeChargers)
          fetchLastAndInprogress(homeChargers)
        }
      } catch {}
    };
    webSocket.addEventListener('message', handler);
    return () => webSocket.removeEventListener('message', handler);
  }, [webSocket, refetchBookings, activeBookings]);

  const buildConnector = (charger, connector) => ({
    id: charger._id,
    bookedSlots: [],
    title: charger.chargerTag,
    connectorType: connector?.type,
    chargerStatus: connector?.chargerStatus,
    power: connector?.power,
    dynamicRates: charger.dynamicRates,
    status: connector?.status,
    rateType: charger.rateType,
    normalRate: charger.rate?.price_components?.price,
    tax: charger.rate?.price_components?.tax,
    nonReservable: true,
    rate: charger.rate,
    connectorID: connector?._id,
    connectorNumber: connector?.connectorNumber,
    noOfConnectorsInCharger: connector?.noOfConnectorsInCharger,
    orderingNumber: charger.orderingNumber,
    chargerLevel: charger?.chargerLevel,
  });

  const handleStart = async (charger, connector) => {
    const key = charger._id;
    setSubmitting((p) => ({ ...p, [key]: true }));
    try {
      const connObj = buildConnector(charger, connector);
      const res = await BreathAPIs.createAutoPayNonResBooking(connObj, new Date(), subLocations[0]?.timezone);
      if (res.status === 200) {
        const bookingId =
          res.data?.booking?._id || res.data?._id || res.data?.bookingID || null;
        setActiveSessions((p) => ({ ...p, [key]: bookingId || true }));
        setActiveBookings([...activeBookings, res.data?.booking])
        toast.success('Session started');
        refetchBookings?.();
        setTimeout(() => fetchLastAndInprogress(homeChargers), 2000);
      }
    } catch {
      toast.error('Failed to start session');
    } finally {
      setSubmitting((p) => ({ ...p, [key]: false }));
    }
  };

  const handleStop = async (chargerId) => {
    setHistoryLoading(true)
    setRecentSessionLoading(true)
    const bookingId = activeSessions[chargerId];
    if (!bookingId || bookingId === true) {
      // bookingId not known — try to find it from refreshed history
      await fetchLastAndInprogress(homeChargers);
      return;
    }
    setSubmitting((p) => ({ ...p, [chargerId]: true }));
    try {
      let response = await BreathAPIs.endBooking(bookingId, userID, new Date());
      setActiveSessions((p) => { const n = { ...p }; delete n[chargerId]; return n; });
      let activeBookingData = [...activeBookings]
      setActiveBookings(activeBookingData.filter((b) => {return b._id !== bookingId}))
      setLivePower((p) => { const n = { ...p }; delete n[chargerId]; console.log("live power after stop : ", n); return n; });
      setLiveEnergy((p) => { const n = { ...p }; delete n[chargerId]; console.log("live energy after stop : ", n); return n; });
      refetchBookings?.();
      setTimeout(() => {
        fetchLastAndInprogress(homeChargers);
        fetchHistory(homeChargers);
      }, 2000);
    } catch {
      toast.error('Failed to stop session');
      setHistoryLoading(false);
      setRecentSessionLoading(false);
    } finally {
      setSubmitting((p) => ({ ...p, [chargerId]: false }));
    }
  };

  const fmtDate = (iso) => {
    const d = new Date(iso);
    return (
      d.toLocaleDateString([], { month: 'short', day: 'numeric' }) +
      ' ' +
      d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    );
  };

  const fmtDuration = (start, end) => {
    const ms = new Date(end) - new Date(start);
    if (ms <= 0) return '-';
    const h = Math.floor(ms / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };

  const statusClass = (s) => {
    if (s === 'Charging') return styles.charging;
    if (s === 'Preparing') return styles.preparing;
    if (s === 'Finishing') return styles.finishing;
    if (s === 'SuspendedEV') return styles.suspendedEV;
    if (s === 'Offline') return styles.offline
    return '';
  };

  const statusLabel = (s) => (s === 'SuspendedEV' ? 'Standby' : s || 'Unknown');

  if (!homeLocationId) {
    return <div className={styles.container}><p className={styles.empty}>No home location found.</p></div>;
  }

  if (homeChargers === null) {
    return <div className={styles.container}><p className={styles.empty}>Loading...</p></div>;
  }

  if (homeChargers.length === 0) {
    return <div className={styles.container}><p className={styles.empty}>No chargers at your home location.</p></div>;
  }
 
  const pastSessions = history
  // .filter((b) => {
  //   const cid = typeof b.charger === 'object' ? b.charger?._id : b.charger;
  //   return !activeSessions[cid];
  // });

  // ── Insights helpers ────────────────────────────────────────────────────────

  


  

  const getPeriodLabel = (p, offset) => {
    const { start, end } = getPeriodRange(p, offset);
    if (p === 'week') {
      const s = start.toLocaleDateString([], { day: 'numeric', month: 'short' });
      const e = end.toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' });
      return `${s} – ${e}`;
    }
    if (p === 'month') return start.toLocaleDateString([], { month: 'long', year: 'numeric' });
    return String(start.getFullYear());
  };

  const estimateEnergy = (session) => {
    const cid = typeof session.charger === 'object' ? session.charger?._id : session.charger;
    const charger = homeChargers?.find((c) => c._id === cid);
    const power = parseFloat(charger?.connector?.[0]?.power) || 0;
    const hrs = Math.max(0, (new Date(session.endTime) - new Date(session.startTime)) / 3600000);
    return power * hrs;
  };

  const buildChartData = (p, offset) => {
    const { start, end } = getPeriodRange(p, offset);
    const inRange = pastSessions.filter((s) => {
      const t = new Date(s.startTime);
      return t >= start && t <= end;
    });

    if (p === 'week') {
      return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((label, i) => {
        const day = inRange.filter((s) => new Date(s.startTime).getDay() === i);
        return {
          label,
          energy: parseFloat(day.reduce((sum, s) => sum + (s.utilizedEnergy), 0).toFixed(4)),
          cost: parseFloat(day.reduce((sum, s) => sum + (s.totalCost || s.estimateAmount || 0), 0).toFixed(2)),
        };
      });
    }
    if (p === 'month') {
      const days = end.getDate();
      return Array.from({ length: days }, (_, i) => {
        const day = inRange.filter((s) => new Date(s.startTime).getDate() === i + 1);
        return {
          label: String(i + 1),
          energy: parseFloat(day.reduce((sum, s) => sum + (s.utilizedEnergy), 0).toFixed(4)),
          cost: parseFloat(day.reduce((sum, s) => sum + (s.totalCost || s.estimateAmount || 0), 0).toFixed(2)),
        };
      });
    }
    return ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map((label, i) => {
      const mo = inRange.filter((s) => new Date(s.startTime).getMonth() === i);
      return {
        label,
        energy: parseFloat(mo.reduce((sum, s) => sum + (s.utilizedEnergy), 0).toFixed(4)),
        cost: parseFloat(mo.reduce((sum, s) => sum + (s.totalCost || s.estimateAmount || 0), 0).toFixed(2)),
      };
    });
  };

  const { start: pStart, end: pEnd } = getPeriodRange(period, periodOffset);
  const inRangeSessions = pastSessions.filter((s) => {
    const t = new Date(s.startTime);
    return t >= pStart && t <= pEnd;
  });
  const totalEnergy = inRangeSessions.reduce((sum, s) => sum + (s.utilizedEnergy || 0), 0);
  const totalCost = inRangeSessions.reduce((sum, s) => sum + (s.totalCost || 0), 0);
  

  const homeLocation = subLocations?.[0];
  const homeRevenueModel = lastSessionData?.billing?.revenueModel
  // homeLocation?.revenueModel?.[homeLocation.revenueModel.length - 1];
  const showCostSplit = homeLocation?.showRateSplitInfo && homeRevenueModel?.revenueType === 'rev_share';
  const towardsPropertyCost = showCostSplit ? (totalCost - lastSessionData.billing?.taxCost - lastSessionData.billing?.IdleCost) * homeRevenueModel.RSF : 0;
  const towardsBreatheEVCost = showCostSplit ? totalCost - towardsPropertyCost : 0;
  const avgDurationMs = inRangeSessions.length
    ? inRangeSessions.reduce((sum, s) => sum + Math.max(0, new Date(s.endTime) - new Date(s.startTime)), 0) / inRangeSessions.length
    : 0;
  const chartData = buildChartData(period, periodOffset);
  const periodLabel = getPeriodLabel(period, periodOffset);

  const fmtAvgDuration = (ms) => {
    const h = Math.floor(ms / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };

  const getChargerTag = (session) => {
    if (!session) return '';
    if (session.chargerTag) return session.chargerTag;
    if (typeof session.charger === 'object' && session.charger?.chargerTag) {
      return session.charger.chargerTag;
    }
    const cid = typeof session.charger === 'object' ? session.charger?._id : session.charger;
    if (cid) {
      const found = homeChargers?.find((c) => String(c._id) === String(cid));
      if (found?.chargerTag) return found.chargerTag;
    }
    if (session.connector) {
      const connId = typeof session.connector === 'object' ? session.connector?._id : session.connector;
      if (connId) {
        const foundByConn = homeChargers?.find((c) => c.connector?.some((conn) => String(conn._id) === String(connId)));
        if (foundByConn?.chargerTag) return foundByConn.chargerTag;
      }
    }
    return '';
  };

  const fetchBookingHistory = async (page = 1, pageSize = 4) => {
    setBookingHistoryLoading(true);
    try {
      if (!homeChargers || homeChargers.length === 0) return;
      const homeIds = new Set(homeChargers.map((c) => String(c._id)));
      const homeIdsArray = Array.from(homeIds)
      if (homeChargers.length > 0) {
        const chargerId = homeChargers[0]._id;
        const res = await BreathAPIs.getBookingHistory(page, pageSize, homeIdsArray);
        const { total, page: pg, pageSize: ps, bookings = [] } = res.data || {};
        const filtered = bookings.filter((b) => {
          const cid = typeof b.charger === 'object' ? b.charger?._id : b.charger;
          return homeIdsArray.some((id)=> String(id) == String(cid))
          // String(cid) === String(chargerId);
        });
        setBookingHistory(filtered);
        setBookingHistoryMeta({ total: total || filtered.length, page: pg || page, pageSize: ps || pageSize });
      } else {
        const res = await BreathAPIs.getBookingHistory(page, pageSize * 2);
        const { total, page: pg, pageSize: ps, bookings = [] } = res.data || {};
        const filtered = bookings.filter((b) => {
          const cid = typeof b.charger === 'object' ? b.charger?._id : b.charger;
          return homeIds.has(String(cid));
        });
        setBookingHistory(filtered.slice(0, pageSize));
        setBookingHistoryMeta({ total: total || filtered.length, page: pg || page, pageSize: ps || pageSize });
      }
    } catch (error) {
      toast.error('Failed to fetch booking history');
      setBookingHistory([]);
    } finally {
      setBookingHistoryLoading(false);
    }
  };
  const handleUnlockConnector = async (charger) => {
    const key = charger._id;
    setUnlocking((p) => ({ ...p, [key]: true }));
    try {
      await BreathAPIs.chargerLocker(charger._id, 'unlock');
      toast.success('Unlock command sent');
      await fetchHomeChargers();
      return true;
    } catch (error) {
      toast.error('Failed to unlock charger');
      return false;
    } finally {
      setUnlocking((p) => ({ ...p, [key]: false }));
    }
  }

  const handleLockConnector = async (charger) => {
    const key = charger._id;
    setUnlocking((p) => ({ ...p, [key]: true }));
    try {
      await BreathAPIs.chargerLocker(charger._id, 'lock');
      toast.success('Charger locked');
      await fetchHomeChargers();
    } catch (error) {
      toast.error('Failed to lock charger');
    } finally {
      setUnlocking((p) => ({ ...p, [key]: false }));
    }
  }

  const confirmUnlockConnector = async () => {
    if (!unlockModalCharger || !isAutoPayEnabledForUser) return;
    const success = await handleUnlockConnector(unlockModalCharger);
    if (success) setUnlockModalCharger(null);
  }

  const totalChargers = homeChargers?.reduce((sum, c) => sum + (c.connector?.length || 1), 0) || 0;
  const isEvenChargers = totalChargers % 2 === 0;
  const hasMultipleChargers = (homeChargers?.length || 0) > 1;
  const hasLastSession = Boolean(lastSessionData && (lastSessionData.startTime || lastSessionData._id));
  const PAGE_SIZE = 4;
  const currentApiPage = bookingHistoryMeta?.page || 1;
  const totalCount = bookingHistoryMeta?.total || 0;
  const apiPageSize = bookingHistoryMeta?.pageSize || PAGE_SIZE;
  const totalPages = Math.max(1, Math.ceil(totalCount / apiPageSize));
  const chargerSessions = bookingHistory || [];

  return (
    <>
    { subLocations[0].active ?
    <div className={styles.container}>
      <div className={styles.chargerGrid}>
        {homeChargers.map((charger) =>
          charger.connector.map((conn, ci) => {
            const isActive = !!activeSessions[charger._id];
            const busy = !!submitting[charger._id];
            const isUnlocked = !!charger.unlockedBy;
            const canStart = (conn.status === 'Finishing' || conn.status === 'Preparing') && charger.chargerStatus == "Online";
            const canStop = conn.status === 'Charging' && charger.chargerStatus == "Online";
            const showLiveEnergyData = conn.status === 'Charging'

            const hasBooking = onGoingUserBookings.some((b) => {
              const cid = typeof b.connector === 'object' ? b.connector._id : b.connector;
              return String(cid) === String(conn._id);
            });
            const enabled = !busy && canStop && conn.status === 'Charging' && hasBooking;

            return (
              <div key={`${charger._id}-${ci}`} className={`${styles.card} ${isActive ? styles.cardActive : ''}`}>
                <div className={styles.content}>
                  <div className={styles.topPanel}>
                    <div>
                      <span className={`${styles.statusPill} ${statusClass(charger.chargerStatus == "Online" ? conn.status : "Offline")}`}>
                        {statusLabel(charger.chargerStatus == "Online" ? conn.status : "Offline")}
                      </span>
                      {
                      (conn.status === 'Charging' && enabled) && (
                        <div>
                          <FontAwesomeIcon icon={faBolt} className={`${styles.chargingAnimation} ${styles.iconSpacing}`} />
                        </div>
                      )}
                    </div>
                    
                    <div className={styles.chargerDetailsCntr}>
                      <div>
                        <div className={styles['chargerHeader']}>
                          <div className={styles['refresh-icon-div']}>
                            <HiRefresh className={refreshing ? styles["spin"] : ""}
                              onClick={() => fetchHomeChargers()} />
                          </div>
                          <div className={styles.chargerName}>
                            {charger.chargerTag}
                          </div>

                        </div>
                        {conn.status === 'Available' &&  (
                          <div>
                            <button className={styles.unlockBtn}
                            onClick={() => isUnlocked ? handleLockConnector(charger) : setUnlockModalCharger(charger)}
                            disabled={!!unlocking[charger._id]}>
                              {unlocking[charger._id] ? (
                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                              ) : (
                                isUnlocked ? 'Lock Charger' : 'Unlock Charger'
                              )}
                            </button>
                          </div>
                        )}
                      </div>
                      {(showLiveEnergyData && enabled) && (
                        
                          <div className={styles.liveStats}>
                            <div className={styles.liveStat}>
                              <span className={styles.liveValue}>
                                {livePower[charger._id] != null ? parseFloat(livePower[charger._id]).toFixed(2) : '—'}
                              </span>
                              <span className={styles.liveUnit}>kW</span>
                              <span className={styles.liveStatLabel}>Power</span>
                            </div>
                            <div className={styles.liveStat}>
                              <span className={styles.liveValue}>
                                {liveEnergy[charger._id] != null ? parseFloat(liveEnergy[charger._id]).toFixed(4) : '—'}
                              </span>
                              <span className={styles.liveUnit}>kWh</span>
                              <span className={styles.liveStatLabel}>Energy</span>
                            </div>
                          </div>

                      )}
                    </div>
                  </div>
                  {!isAutoPayEnabledForUser && !isUnlocked && <div className={styles.autoPayText}>Enable auto reload from <Link to="/profile" className={styles.autoPayTextLink}>Profile</Link> or Wallet to start charging
                    </div>}
                    { isAutoPayEnabledForUser && !isUnlocked && conn.status === 'Preparing' && charger.chargerStatus == "Online" &&
                      <div className={styles.autoPayText}>
                      <span className={`${styles['bottom-banner']}`}>
                                        {/* <IoInformationCircle /> */}
                                         Press <VscDebugStart /> to start charge
                                      </span>
                                      </div>
                    }
                    { isAutoPayEnabledForUser && !isUnlocked && conn.status === 'Available' && charger.chargerStatus == "Online" &&
                      <div className={styles.autoPayText}><span className={`${styles['bottom-banner']}`}>
                      
                                    Press <b style={{ padding: '0 2px' }}> refresh </b> once plugged-in to vehicle.
                                  </span> </div>
                    }

                  <div className={styles.bottomPanel}>
                    { isUnlocked ? (
                      showLiveEnergyData ? (
                        <div className={styles.cardActions}>
                          <button
                            className={`btn btn-danger ${styles.actionBtn}`}
                            onClick={() => handleStop(charger._id)}
                            disabled={!enabled || !isAutoPayEnabledForUser}
                          >
                            {
                              busy ?
                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                            :
                            <PiStopBold />}
                          </button>
                        </div>
                      ) : (
                        <div className={styles.plugInText}>{charger?.chargerStatus === 'Online' ? "Plug-in to vehicle to start" : ""}</div>
                      )
                    ) : (
                      <div className={styles.cardActions}>
                      <button
                        className={`btn btn-success ${styles.actionBtn} ${ !(busy || !canStart || !isAutoPayEnabledForUser) ? styles.startPulse : '' }`}
                        onClick={() => handleStart(charger, conn)}
                        disabled={ busy || !canStart || !isAutoPayEnabledForUser}
                      >
                        {
                        busy ?
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                        :
                        <VscDebugStart />}
                      </button>
                      <button
                        className={`btn btn-danger ${styles.actionBtn}`}
                        onClick={() => handleStop(charger._id)}
                        disabled={!enabled || !isAutoPayEnabledForUser }
                      >
                        {
                          busy ?
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                        :
                        <PiStopBold />}
                      </button>
                    </div>
                    )}
                    <div className={styles.bottomInfo}>
                      <span><PiPlugChargingFill /> {conn.type}</span>
                      <span><AiFillThunderbolt /> {conn.power} kW</span>
                    </div>
                  </div>
                </div>
                <div className={styles.idleFeeBanner}><IoInformationCircle /> Idle fee not applicable</div>
              </div>
            );
          })
        )}

        {/* ── Single Last session / Recent sessions details block ── */}
        <div className={`${styles.sessionDetails} ${isEvenChargers ? styles.sessionDetailsFull : ''}`}>
          {recentSessionLoading ? (
            <p className={styles.detailEmpty}>Loading...</p>
          ) : showRecent ? (
            <>
              <div className={styles.detailHeader}>
                <div className={styles.detailTitle}>Recent Sessions</div>
                <div
                  className={styles.viewMore}
                  style={{ cursor: 'pointer' }}
                  onClick={() => setShowRecent(false)}
                >← Back</div>
              </div>
              {bookingHistoryLoading ? (
                <p className={styles.detailEmpty}>Loading...</p>
              ) : chargerSessions.length === 0 ? (
                <p className={styles.detailEmpty}>No sessions yet</p>
              ) : (
                <>
                  <div className={styles.recentList}>
                    {chargerSessions.map((s, i) => {
                      const sessionCost = s.totalCost != null ? parseFloat(s.totalCost) : null;
                      const sessionTowardsProperty = showCostSplit && sessionCost != null ? (sessionCost - (s.billing?.taxCost || 0) - (s.billing?.IdleCost || 0)) * (homeRevenueModel?.RSF || 0) : 0;
                      const sessionTowardsBreatheEV = showCostSplit && sessionCost != null ? sessionCost - sessionTowardsProperty : 0;
                      const sessionChargerTag = getChargerTag(s);
                      return (
                        <div key={s._id || i} className={styles.recentRowGroup}>
                          <div className={styles.recentRow}>
                            <span className={styles.recentDateGroup}>
                              {hasMultipleChargers && (
                                <span className={styles.recentChargerTag}>
                                  {sessionChargerTag || 'Charger'}
                                </span>
                              )}
                              <span className={styles.recentDate}>{fmtDate(s.startTime)}</span>
                              <span className={styles.recentDuration}>{fmtDuration(s.startTime, s.endTime)}</span>
                            </span>
                            <span className={styles.recentValueGroup}>
                              <span className={styles.recentEnergy}>{(s.utilizedEnergy || 0).toFixed(4)} kWh</span>
                              <span className={styles.recentCost}>
                                {sessionCost != null
                                  ? `$${sessionCost.toFixed(2)}`
                                  : s.estimateAmount != null
                                  ? `~$${parseFloat(s.estimateAmount).toFixed(2)}`
                                  : ''}
                              </span>
                            </span>
                          </div>
                          {showCostSplit && sessionCost != null && (
                            <div className={styles.recentSplit}>
                              <span>Property ${sessionTowardsProperty.toFixed(2)}</span>
                              <span className={styles.recentSplitDot}>&middot;</span>
                              <span>BreatheEV ${sessionTowardsBreatheEV.toFixed(2)}</span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div className={styles.paginationRow}>
                    <button
                      className={styles.pageBtn}
                      onClick={() => fetchBookingHistory(currentApiPage - 1, PAGE_SIZE)}
                      disabled={currentApiPage <= 1}
                    >‹ Prev</button>
                    <span className={styles.pageInfo}>Page {currentApiPage} of {totalPages}</span>
                    <button
                      className={styles.pageBtn}
                      onClick={() => fetchBookingHistory(currentApiPage + 1, PAGE_SIZE)}
                      disabled={currentApiPage >= totalPages}
                    >Next ›</button>
                  </div>
                </>
              )}
            </>
          ) : hasLastSession ? (
            <>
              <div className={styles.detailHeader}>
                <div className={styles.detailTitle}>Last Session</div>
                <div
                  className={styles.viewMore}
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    fetchBookingHistory(1, PAGE_SIZE);
                    setShowRecent(true);
                  }}
                >+ View More</div>
              </div>
              <div className={`${styles.detailList} ${isEvenChargers ? styles.detailListFull : ''}`}>
                {hasMultipleChargers && (
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>Charger</span>
                    <span className={styles.detailValue}>{getChargerTag(lastSessionData) || '—'}</span>
                  </div>
                )}
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Start</span>
                  <span className={styles.detailValue}>{fmtDate(lastSessionData.startTime)}</span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>End</span>
                  <span className={styles.detailValue}>{lastSessionData.endTime ? fmtDate(lastSessionData.endTime) : '—'}</span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Duration</span>
                  <span className={styles.detailValue}>{fmtDuration(lastSessionData.startTime, lastSessionData.endTime)}</span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Energy</span>
                  <span className={styles.detailValue}>
                    {lastSessionData.utilizedEnergy ? `${parseFloat(lastSessionData.utilizedEnergy).toFixed(4)} kWh` : '—'}
                  </span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Total Cost</span>
                  <span className={styles.detailValue}>
                    {lastSessionData.totalCost != null
                      ? `$${parseFloat(lastSessionData.totalCost).toFixed(2)}` : '—'}
                  </span>
                </div>
                {showCostSplit && lastSessionData.totalCost != null && (
                  <>
                    <div className={`${styles.detailRow} ${styles.detailRowSub}`}>
                      <span className={styles.detailLabel}>Property Energy Cost</span>
                      <span className={styles.detailValue}>${(parseFloat(lastSessionData.totalCost - (lastSessionData.billing?.taxCost || 0) - (lastSessionData.billing?.IdleCost || 0)) * (homeRevenueModel?.RSF || 0)).toFixed(2)}</span>
                    </div>
                    <div className={`${styles.detailRow} ${styles.detailRowSub}`}>
                      <span className={styles.detailLabel}>BreatheEV Fee</span>
                      <span className={styles.detailValue}>${(parseFloat(lastSessionData.totalCost) - (parseFloat(lastSessionData.totalCost - (lastSessionData.billing?.taxCost || 0) - (lastSessionData.billing?.IdleCost || 0)) * (homeRevenueModel?.RSF || 0))).toFixed(2)}</span>
                    </div>
                  </>
                )}
              </div>
            </>
          ) : (
            <p className={styles.detailEmpty}>No sessions yet</p>
          )}
        </div>
      </div>

      <div className={styles.insightsSection}>
        {/* Period tabs + nav */}
        <div className={styles.periodHeader}>
          <div className={styles.periodTabs}>
            {['week', 'month', 'year'].map((p) => (
              <button
                key={p}
                className={`${styles.periodTab} ${period === p ? styles.periodTabActive : ''}`}
                onClick={() => { setPeriod(p); setPeriodOffset(0); }}
              >
                {p.charAt(0).toUpperCase() + p.slice(1)}
              </button>
            ))}
          </div>
          <div className={styles.periodNav}>
            <button className={styles.navBtn} onClick={() => setPeriodOffset((o) => o - 1)}>
              <MdChevronLeft />
            </button>
            <span className={styles.periodLabel}>{periodLabel}</span>
            <button className={styles.navBtn} onClick={() => setPeriodOffset((o) => o + 1)} disabled={periodOffset >= 0}>
              <MdChevronRight />
            </button>
          </div>
        </div>

        {/* Summary stats */}
        <div className={styles.summaryRow}>
          <div className={styles.summaryItem}>
            <div className={styles.summaryValue}>{totalEnergy.toFixed(4)} <span className={styles.summaryUnit}>kWh</span></div>
            <div className={styles.summaryLabel}>Energy</div>
          </div>
          <div className={styles.summaryDivider} />
          <div className={styles.summaryItem}>
            <div className={styles.summaryValue}>${totalCost.toFixed(2)}</div>
            <div className={styles.summaryLabel}>Total Spent</div>
          </div>
          <div className={styles.summaryDivider} />
          <div className={styles.summaryItem}>
            <div className={styles.summaryValue}>{inRangeSessions.length > 0 ? fmtAvgDuration(avgDurationMs) : '—'}</div>
            <div className={styles.summaryLabel}>Avg Duration</div>
          </div>
        </div>

        {/* Bar chart */}
        <div className={styles.chartWrap}>
          {historyLoading ? (
            <p className={styles.empty}>Loading...</p>
          ) : (
            <ResponsiveContainer width="100%" height={160}>
              <BarChart data={chartData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7ea" />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#777' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#777' }} axisLine={false} tickLine={false} />
                <Tooltip
                  formatter={(v) => [`${v.toFixed(4)} kWh`, 'Energy']}
                  contentStyle={{ fontSize: 12, borderRadius: 6, border: '1px solid #e5e7ea' }}
                />
                <Bar dataKey="energy" fill="#50B965" radius={[4, 4, 0, 0]} maxBarSize={32} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {!historyLoading && inRangeSessions.length === 0 && (
          <p className={styles.empty}>No sessions in this period.</p>
        )}
      </div>

      <Modal
        title="Unlock Charger"
        open={!!unlockModalCharger}
        onCancel={() => setUnlockModalCharger(null)}
        okText="Unlock"
        okButtonProps={{
          danger: true,
          disabled: !isAutoPayEnabledForUser,
          loading: !!unlocking[unlockModalCharger?._id],
        }}
        onOk={confirmUnlockConnector}
      >
        <p>
          Unlocking <b>{unlockModalCharger?.chargerTag}</b> will make you responsible for
          all bookings and billing on this charger until it is locked again.
        </p>
        {!isAutoPayEnabledForUser && (
          <p className={styles.autoPayWarning}>
            Please enable AutoPay from <Link to="/profile" className={styles.autoPayTextLink}>Profile</Link> or Wallet to unlock this charger.
          </p>
        )}
      </Modal>
    </div>
    :
    <><div className={styles['breathe-card']}>

                <div className={styles['breathe-pulse']} />

                <div className={styles['breathe-content']}>
                    <span className={styles['breathe-brand']}>Charging Reimagined</span>
                    <h3 className={styles['breathe-headline']}>BREATHE EV</h3>
                    <span className={styles['breathe-status']}>Coming Soon</span>
                </div>
            </div></>}
    </>
  );
};

HomeCharging.propTypes = {
  userID: PropTypes.string,
  subLocations: PropTypes.array,
  isAutoPayEnabledForUser: PropTypes.bool,
};

const mapStateToProps = (state) => ({
  userID: state.auth && state.auth.user ? state.auth.user._id : null,
  isAutoPayEnabledForUser: state.auth && state.auth.user ? state.auth.user.isAutoPayEnabled : false,
});

export default connect(mapStateToProps, {})(HomeCharging);
